import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import partial from 'lodash/partial';
import includes from 'lodash/includes';
import * as ResourceApi from '../../api/resources';
import * as ResourceActions from 'resources/actions';
import * as LegacyUserApi from '../../api/user-management-legacy';
import * as UserService from 'api/user-management-service';
import {deleteCompleteAction} from 'pages/DeleteConfirmation/actions';
import {createResourcePageSelector} from 'resources/selectors';
import {buildQueryFilters, getSearchableUserResources} from './utils';
import {filterOptionsFactory} from '../../viewOptions/factories';
import {createResourceSortOptionsSelector} from 'viewOptions/selectors';
import {Types as ViewOptionsActions} from 'viewOptions/actions';
import userMgmtConfigs from './configs';

import {
    createUserSuccessAction,
    endAwardDaysAction,
    endAwardPointsAction,
    endAwardSpinsAction,
    endChangeEmailAction,
    endChangePasswordAction,
    endCreateUserAction,
    endDeleteUserDataAction,
    endRepairProfileAction,
    endRevokeAmountOfPointsAction,
    endRevokePointsAction,
    errorAwardingPointsAction,
    failedPointHistoryAction,
    failedRaffleEntriesAction,
    failedStartsCompletesAction,
    failedUserResourceAction,
    fetchUserAction,
    receivedPointHistoryAction,
    receivedRaffleEntriesAction,
    receivedStartsCompletesAction,
    receivedUserResourceAction,
    setVisibleColumnsModalAction,
    Types as ActionTypes,
    userFetchesCompleteAction
} from './actions';

const handleErrorAction = error => Observable.of({type: 'ERROR', error});

export const saveVisibleColumnsEpic = action$ => action$.ofType(ActionTypes.SAVE_VISIBLE_COLUMNS)
  .mapTo(setVisibleColumnsModalAction(false));

const createUserEpic = action$ => action$.ofType(ActionTypes.SUBMIT_CREATE_USER)
  .mergeMap(action => {
    // lang doesn't matter at the moment, but the API wants it anyway.
    const payload = {...action.formData, lang: 'en-US'};

    const userCreation$ = ResourceApi.create(ResourceApi.Types.User, payload, []);

    return userCreation$
      .mergeMap(({response}) => Observable.of(
        createUserSuccessAction(response.object.id),
        endCreateUserAction()
      ))
      .catch(handleErrorAction);
  });

const getResourcePage = createResourcePageSelector(ResourceApi.Types.User);
const getSortOptions = createResourceSortOptionsSelector(ResourceApi.Types.User);
const SearchableUserResources = getSearchableUserResources(userMgmtConfigs);

// executes a search with the current query on toggling sort direction or paging through results
const withCurrentQueryEpic = (action$, store) => action$.ofType(
  ViewOptionsActions.TOGGLE_SORT_FOR_RESOURCE,
  ResourceActions.Types.UPDATE_PAGE_NUMBER_FOR_RESOURCE
)
  .filter(action => includes(SearchableUserResources, action.resourceType))
  .map(action => {
    const state = store.getState();
    const {count, page} = getResourcePage(state.resources);
    const sortOptions = getSortOptions(state);
    const filterOptions = filterOptionsFactory({
      filters: buildQueryFilters(state.userManagement.currentQuery)
    });

    return ResourceActions.getResourcePageAction(
      action.resourceType,
      count,
      (action.page || page),
      sortOptions,
      filterOptions
    );
  });

// user data API request executors and their corresponding success/error handlers:
const UserDetailRequests = [
  {
    execRequest: partial(ResourceApi.getById, ResourceApi.Types.User),
    onSuccess: ({response}) => receivedUserResourceAction(response.object),
    onError: failedUserResourceAction
  },
  {
    execRequest: LegacyUserApi.getStartsCompletes,
    onSuccess: ({response}) => receivedStartsCompletesAction(response),
    onError: failedStartsCompletesAction,
  },
  {
    execRequest: LegacyUserApi.getRaffleEntries,
    onSuccess: ({response}) => receivedRaffleEntriesAction(response.entries),
    onError: failedRaffleEntriesAction
  },
  // {
  //   execRequest: (uid) => console.log("move else where"),
  //   onSuccess: ({response}) => receivedPrizeboothOrdersAction(response.object || []),
  //   onError: failedPrizeboothOrdersAction
  // },
  {
    execRequest: LegacyUserApi.getPointHistory,
    onSuccess: ({response}) => receivedPointHistoryAction(response.coinHistory),
    onError: failedPointHistoryAction
  }
];

const fetchUserEpic = (action$, store) => action$.ofType(ActionTypes.REQUEST_USER_DETAILS)
  .filter(action => Boolean(action.userId))
  .mergeMap(action => {

    // passing these requests to forkJoin will run them in parallel, but mapping each
    // inner observable to its respective success/error handlers individually allows each
    // user detail tab to render independently.

    const userDataFetches$ = Observable.forkJoin(UserDetailRequests.map(({execRequest, onSuccess, onError}) => {
      const userDataFetch$ = execRequest(action.userId);

      return userDataFetch$
        .map(onSuccess)
        .catch(ajaxError => Observable.of(onError(ajaxError)));
    }));

    return userDataFetches$
      .do(_ => {
        // signal that all requests have completed, whether they failed or not, as a side-effect.
        store.dispatch(userFetchesCompleteAction());
      })
      .switch() // serially emit each response action, whether success or error.
      .catch(handleErrorAction);
  });

const awardPointsEpic = action$ => action$.ofType(ActionTypes.SUBMIT_AWARD_POINTS)
  .mergeMap(action => {
    const awardPointsRequest$ = UserService.awardPoints(action.uid, action.amount, action.reason);

    return awardPointsRequest$
      .mergeMap(() => Observable.of(fetchUserAction(action.uid), endAwardPointsAction()))
      .catch(err => {
          if (err.xhr) {
              if (err.response.messages.reason === "MissingInfo") {
                  let errorMessage = `userManagement.userDetail.modals.awardPoints.errors.missing.field.${err.response.missingInfoLabels[0]}`;
                  return Observable.of(errorAwardingPointsAction(errorMessage));
              }
          } else {
              return handleErrorAction(err);
          }

          return handleErrorAction(err);
      });
  });

const awardSpinsEpic = action$ => action$.ofType(ActionTypes.SUBMIT_AWARD_SPINS)
  .mergeMap(action => {
    const awardSpinsRequest$ = UserService.awardSpins(action.uid, action.amount);

    return awardSpinsRequest$
      .mergeMap(() => Observable.of(fetchUserAction(action.uid), endAwardSpinsAction()))
      .catch(handleErrorAction);
  });

const awardDaysEpic = action$ => action$.ofType(ActionTypes.SUBMIT_AWARD_DAYS)
  .mergeMap(action => {
    const awardDaysRequest$ = UserService.awardConsecutiveDays(action.uid, action.amount);

    return awardDaysRequest$
      .mergeMap(() => Observable.of(fetchUserAction(action.uid), endAwardDaysAction()))
      .catch(handleErrorAction);
  });

const changeEmailEpic = action$ => action$.ofType(ActionTypes.SUBMIT_CHANGE_EMAIL)
  .mergeMap(action => {
    const changeEmailRequest$ = UserService.changeEmail(
      action.uid,
      action.newEmail,
    );

  return changeEmailRequest$
    .mergeMap(() => Observable.of(fetchUserAction(action.uid), endChangeEmailAction()))
    .catch(handleErrorAction);
});

const changePasswordEpic = action$ => action$.ofType(ActionTypes.SUBMIT_CHANGE_PASSWORD)
  .mergeMap(action => {
    const changePasswordRequest$ = UserService.changePassword(
      action.uid,
      action.newPassword,
      action.newPasswordConfirm
    );

    return changePasswordRequest$
      .map(endChangePasswordAction)
      .catch(handleErrorAction);
  });

const deleteUserEpic = (action$, store) => action$.ofType(ActionTypes.SUBMIT_DELETE_USER)
  .mergeMap(action => {
    const {deleteResourceId, postDeleteActions} = store.getState().deleteConfirmation;
    const postActions = [deleteCompleteAction()].concat(postDeleteActions);

    const deleteUserRequest$ = UserService.deactivateUser(Number(deleteResourceId.id));

    return deleteUserRequest$
      .mergeMap(() => Observable.of(...postActions))
      .catch(handleErrorAction);
  });

const undeleteUserEpic = (action$, store) => action$.ofType(ActionTypes.SUBMIT_UNDELETE_USER)
  .mergeMap(action => {
    const {deleteResourceId, postDeleteActions} = store.getState().deleteConfirmation;
    const postActions = [deleteCompleteAction()].concat(postDeleteActions);

    const undeleteUserRequest$ = LegacyUserApi.undeleteUser(Number(deleteResourceId.id));

    return undeleteUserRequest$
      .mergeMap(() => Observable.of(...postActions))
      .catch(handleErrorAction);
  });

const repairProfileEpic = (action$, store) => action$.ofType(ActionTypes.SUBMIT_REPAIR_PROFILE)
  .mergeMap(action => {
    const {currentUserId} = store.getState().userManagement;
    const uid = Number(currentUserId);
    const repairProfileRequest$ = UserService.repairProfile(uid);

    return repairProfileRequest$
      .mergeMap(() => Observable.of(fetchUserAction(uid), endRepairProfileAction()))
      .catch(handleErrorAction);
  });

const revokePointsEpic = (action$, store) => action$.ofType(ActionTypes.SUBMIT_REVOKE_POINTS)
  .mergeMap(action => {
    const {currentUserId, currentRevocation} = store.getState().userManagement;
    const uid = Number(currentUserId);
    const {phid} = currentRevocation;

    const revocationRequest$ = UserService.revokePoints(uid, phid);

    return revocationRequest$
      .mergeMap(() => Observable.of(fetchUserAction(uid), endRevokePointsAction()))
      .catch(handleErrorAction);
  });

  const revokeAmountOfPointsEpic = action$ => action$.ofType(ActionTypes.SUBMIT_REVOKE_AMOUNT_OF_POINTS)
    .mergeMap(action => {
      const revokeAmountOfPointsRequest = UserService.revokeAmountOfPoints(action.uid, action.amount, action.reason);

      return revokeAmountOfPointsRequest
        .mergeMap(() => Observable.of(fetchUserAction(action.uid), endRevokeAmountOfPointsAction()))
        .catch(handleErrorAction);
    });

const deleteUserDataEpic = (action$, store) => action$.ofType(ActionTypes.SUBMIT_DELETE_USER_DATA)
  .mergeMap(action => {
    const userToDeleteData = store.getState().userManagement.userToDeleteData;

    return UserService.deleteUserData(userToDeleteData.id)
      .map(() => endDeleteUserDataAction())
      .catch(handleErrorAction);
  });

export default [
  saveVisibleColumnsEpic,
  createUserEpic,
  withCurrentQueryEpic,
  fetchUserEpic,
  awardPointsEpic,
  awardSpinsEpic,
  awardDaysEpic,
  changeEmailEpic,
  changePasswordEpic,
  repairProfileEpic,
  deleteUserEpic,
  undeleteUserEpic,
  revokePointsEpic,
  revokeAmountOfPointsEpic,
  deleteUserDataEpic
];



// WEBPACK FOOTER //
// ./public/pages/UserManagement/epics.js